import { useCallback, useEffect, useState } from "react";
import { api, apiError, fmt } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Radio, Trash2, Save } from "lucide-react";

const PLATFORMS = [
  ["twitch", "Twitch"],
  ["youtube", "YouTube"],
  ["kick", "Kick"],
];

const BLANK = (slot) => ({
  slot, platform: "twitch", channel: "", title: "", tags: "",
  live: false, active: true,
});

const HINT = {
  twitch: "Twitch channel name, e.g. liquidrp",
  kick: "Kick channel name, e.g. liquidrp",
  youtube: "YouTube channel id (UC…) for their live feed, or a video id",
};

const SlotCard = ({ slot, stream, onSave, onDelete }) => {
  const [draft, setDraft] = useState(stream || BLANK(slot));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setDraft(stream
      ? { ...stream, tags: Array.isArray(stream.tags) ? stream.tags.join(", ") : stream.tags || "" }
      : BLANK(slot));
  }, [stream, slot]);

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const save = async () => {
    if (!draft.channel.trim()) {
      toast.error("Channel is required");
      return;
    }
    setBusy(true);
    try {
      await onSave(stream?.id, { ...draft, slot, active: true });
      toast.success(`Tile ${slot} saved`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-border bg-card p-5" data-testid={`watch-slot-${slot}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          Tile {slot}
        </div>
        <label className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em]">
          <span className={draft.live ? "text-red-400" : "text-muted-foreground"}>Live</span>
          <Switch checked={!!draft.live} onCheckedChange={(v) => set("live", v)}
            data-testid={`watch-live-${slot}`} />
        </label>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {PLATFORMS.map(([v, l]) => (
          <button
            key={v}
            type="button"
            onClick={() => set("platform", v)}
            data-testid={`watch-platform-${slot}-${v}`}
            className={`border px-3 py-1.5 text-[11px] uppercase tracking-[0.16em] transition-colors ${
              draft.platform === v
                ? "border-primary bg-primary/10 text-primary"
                : "border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      <div className="mt-4 space-y-3">
        <div>
          <Input className="rounded-none bg-secondary" placeholder="channel"
            value={draft.channel} onChange={(e) => set("channel", e.target.value)}
            data-testid={`watch-channel-${slot}`} />
          <p className="mt-1.5 text-[11px] text-muted-foreground">{HINT[draft.platform]}</p>
        </div>
        <div>
          <Input className="rounded-none bg-secondary" placeholder="Stream title shown to players"
            value={draft.title} onChange={(e) => set("title", e.target.value)}
            data-testid={`watch-title-${slot}`} />
          <p className="mt-1.5 text-[11px] text-muted-foreground">
            Shown on the tile and above the player — leave blank to use the channel name
          </p>
        </div>
        <div>
          <Input className="rounded-none bg-secondary" placeholder="Liquid RP, GTA RP, English"
            value={draft.tags || ""} onChange={(e) => set("tags", e.target.value)}
            data-testid={`watch-tags-${slot}`} />
          <p className="mt-1.5 text-[11px] text-muted-foreground">
            Comma separated tags shown under the main player
          </p>
          <p className="mt-3 text-[11px] text-muted-foreground">
            Players only see this tile while <span className="text-red-400">Live</span> is on —
            switch it off and it disappears from the Watch page.
          </p>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2">
        <Button className="flex-1 rounded-none" disabled={busy} onClick={save}
          data-testid={`watch-save-${slot}`}>
          <Save className="mr-2 h-3.5 w-3.5" /> {stream ? "Save" : "Add stream"}
        </Button>
        {stream && (
          <Button variant="outline" size="icon" className="rounded-none"
            onClick={() => onDelete(stream.id)} data-testid={`watch-delete-${slot}`}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        )}
      </div>
    </div>
  );
};

export const WatchAdmin = () => {
  const [state, setState] = useState(null);
  const [cfg, setCfg] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/watch");
    setState(data);
    setCfg(data.config);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveStream = async (id, body) => {
    if (id) await api.put(`/admin/watch/streams/${id}`, body);
    else await api.post("/admin/watch/streams", body);
    await load();
  };

  const deleteStream = async (id) => {
    await api.delete(`/admin/watch/streams/${id}`);
    toast.success("Tile cleared");
    load();
  };

  const saveCfg = async () => {
    try {
      const { data } = await api.put("/admin/watch/config", cfg);
      setCfg(data);
      toast.success("Watch settings saved");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!state || !cfg) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const bySlot = (slot) => state.streams.find((s) => s.slot === slot);
  const liveCount = state.streams.filter((s) => s.live).length;

  return (
    <div className="space-y-8" data-testid="watch-admin">
      <div className="border border-border bg-card p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h3 className="flex items-center gap-2 text-base font-semibold md:text-lg">
              <Radio className={`h-4 w-4 ${liveCount ? "text-red-500" : "text-muted-foreground"}`} />
              Watch & earn
            </h3>
            <p className="mt-1 text-xs text-muted-foreground">
              {liveCount > 0
                ? `${liveCount} tile(s) marked live — the Watch tab is visible to players.`
                : "Nothing marked live, so the Watch tab is hidden from players."}
            </p>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <div className="border border-border px-4 py-2" data-testid="watch-stats-today">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                Today
              </div>
              <div className="mono mt-0.5">
                {state.today.watchers} watchers · {state.today.minutes}m ·{" "}
                <span className="text-primary">{fmt(state.today.points)}</span> pts
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            ["points_per_minute", "Points / minute"],
            ["daily_cap_minutes", "Daily cap (minutes)"],
            ["presence_prompt_minutes", "'Still there?' every (min)"],
            ["heartbeat_seconds", "Heartbeat (seconds)"],
          ].map(([k, label]) => (
            <div key={k}>
              <label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                {label}
              </label>
              <Input type="number" className="mono mt-2 rounded-none bg-secondary"
                value={cfg[k]} onChange={(e) => setCfg({ ...cfg, [k]: Number(e.target.value) })}
                data-testid={`watch-cfg-${k}`} />
            </div>
          ))}
        </div>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              Headline
            </label>
            <Input className="mt-2 rounded-none bg-secondary" value={cfg.headline}
              onChange={(e) => setCfg({ ...cfg, headline: e.target.value })}
              data-testid="watch-cfg-headline" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              Blurb
            </label>
            <Input className="mt-2 rounded-none bg-secondary" value={cfg.blurb}
              onChange={(e) => setCfg({ ...cfg, blurb: e.target.value })}
              data-testid="watch-cfg-blurb" />
          </div>
        </div>

        <div className="mt-5 flex items-center justify-between">
          <label className="flex items-center gap-3 text-xs uppercase tracking-[0.18em] text-muted-foreground">
            <Switch checked={!!cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })}
              data-testid="watch-cfg-enabled" />
            Watch & earn enabled
          </label>
          <Button className="rounded-none" onClick={saveCfg} data-testid="watch-cfg-save">
            Save settings
          </Button>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {[1, 2, 3, 4].map((slot) => (
          <SlotCard key={slot} slot={slot} stream={bySlot(slot)} onSave={saveStream}
            onDelete={deleteStream} />
        ))}
      </div>
    </div>
  );
};
